//deps
import React, { memo, useContext, useMemo, useEffect, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";

//components

import { BillToCompanyWidget } from "@swiggy-private/payment-ui";
import { useBillToCompanyDetails } from "@payment-client/hooks/useBillToCompanyDetails";
import { BillToCompanyContext } from "@swiggy-private/checkout-core";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { ExpenseCodeHalfcard } from "@swiggy-private/payment-ui";
import useExpenseCodeHalfcard from "@payment-client/hooks/useExpenseCodeHalfcard";
import useBillToCompanySplitPay from "@payment-client/hooks/useBillToCompanySplitPay";
import { isBusinessLineDineout } from "@payment-client/helpers/Misc";

export interface CartFooterPaymentWidgetsProps {
    setIsTurboPayWidgetHidden?: (isTurboPayWidgetHidden: boolean) => void;
    setForceExtraPaddingOnCart?: (forceExtraPaddingOnCart: boolean) => void;
}

const CartFooterPaymentWidgets = ({
    setIsTurboPayWidgetHidden,
    setForceExtraPaddingOnCart,
}: CartFooterPaymentWidgetsProps) => {
    const { isSelected, setIsSelected } = useContext(BillToCompanyContext);
    const { loadingPayments } = useContext(ShowPaymentContext);
    const { expenseCodeProps, showExpenseCodeHalfCard, onOpenHalfcard } = useExpenseCodeHalfcard();
    const { onToggle, onLoad, billToCompanyDetails, billToCompanyData } = useBillToCompanyDetails(
        showExpenseCodeHalfCard,
        onOpenHalfcard,
        isSelected,
        setIsSelected,
    );

    useBillToCompanySplitPay();

    const prevSelectedRef = useRef(billToCompanyDetails.selected);

    useEffect(() => {
        // If billToCompanyDetails.selected changed, show the turbo pay widget
        if (prevSelectedRef.current !== billToCompanyDetails.selected) {
            setIsTurboPayWidgetHidden?.(false);
            prevSelectedRef.current = billToCompanyDetails.selected;
        }
    }, [billToCompanyDetails.selected, setIsTurboPayWidgetHidden]);

    // TODO: [Milestone 1 - Temporary fix] Remove this dineout-specific hook and implement proper MMT-based
    // B2C eligibility check. The widget should be hidden based on marketplace eligibility configuration
    // rather than hardcoding business line checks.
    const isMarketplaceBusinessLineDineout = isBusinessLineDineout();

    useEffect(() => {
        if (!setForceExtraPaddingOnCart) return;

        const shouldAddPadding = !isEmpty(billToCompanyData);
        setForceExtraPaddingOnCart(shouldAddPadding);

        return () => setForceExtraPaddingOnCart(false);
    }, [billToCompanyData, setForceExtraPaddingOnCart]);

    const billToCompanyWidget = useMemo(() => {
        if (isMarketplaceBusinessLineDineout) {
            return null;
        }
        if (isEmpty(billToCompanyData)) {
            return null;
        }
        return (
            <BillToCompanyWidget
                data={billToCompanyData}
                onToggle={onToggle}
                onLoad={onLoad}
                isSelected={billToCompanyDetails?.selected}
                isPaymentRefetching={loadingPayments}
            />
        );
    }, [billToCompanyData, billToCompanyDetails, onToggle, onLoad, isMarketplaceBusinessLineDineout, loadingPayments]);

    return (
        <>
            {billToCompanyWidget}
            <ExpenseCodeHalfcard {...expenseCodeProps} />
        </>
    );
};

export default memo(CartFooterPaymentWidgets);
